// Sidebar "+" → New uploads (Figma 2727:686): where the new post goes. Personal, Event (3a R8), Books, Apopilo and News are open;
// Blog shows "Soon".
import type { ComponentType } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { BookOpen, CalendarDays, Languages, MonitorPlay, Newspaper, User } from "lucide-react";

const SCRIM = "bg-[#0f172a]/50"; // theme-exempt: modal scrim, as the post dialog's, both themes

interface Props {
  onClose(): void;
  onPersonal(): void;
  onNews(): void;
  onEvent(): void;
  onBook(): void;
  onApopilo(): void;
}

type Row = { id: string; icon: ComponentType<{ size?: number; "aria-hidden"?: boolean; className?: string }>; label: string; enabled: boolean };

export default function NewUploadsModal({ onClose, onPersonal, onNews, onEvent, onBook, onApopilo }: Props) {
  const { t } = useTranslation();
  const rows: Row[] = [
    { id: "personal", icon: User, label: t("personal_tab_personal", "Personal"), enabled: true },
    { id: "event", icon: CalendarDays, label: t("personal_upload_event", "Event"), enabled: true },
    { id: "blog", icon: MonitorPlay, label: t("personal_tab_blog", "Blog"), enabled: false },
    { id: "books", icon: BookOpen, label: t("personal_tab_books", "Books"), enabled: true },
    { id: "apopilo", icon: Languages, label: t("personal_tab_apopilo", "Apopilo"), enabled: true },
    { id: "news", icon: Newspaper, label: t("personal_upload_news", "News · to the MapTxat wall"), enabled: true },
  ];
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={`fixed inset-0 z-[2000] ${SCRIM}`} />
        <Dialog.Content
          data-testid="new-uploads-modal"
          aria-describedby={undefined}
          className="fixed z-[2000] left-1/2 top-20 -translate-x-1/2 md:translate-x-0 md:left-[88px] w-[360px] max-w-[calc(100vw-32px)] rounded-2xl bg-[var(--bg-surface)] shadow-xl py-4"
        >
          <div className="px-4 pb-2">
            <Dialog.Title className="m-0 text-base font-bold text-[var(--foreground)]">{t("personal_new_uploads", "New uploads")}</Dialog.Title>
            <p className="m-0 mt-0.5 text-xs text-[var(--foreground-muted)]">{t("personal_new_uploads_subtitle", "Choose where your post goes")}</p>
          </div>
          <ul className="list-none m-0 p-0">
            {rows.map(({ id, icon: Icon, label, enabled }) => (
              <li key={id}>
                <button
                  type="button"
                  data-testid={`new-upload-${id}`}
                  disabled={!enabled}
                  onClick={() => {
                    if (!enabled) return;
                    if (id === "news") onNews();
                    else if (id === "event") onEvent();
                    else if (id === "books") onBook();
                    else if (id === "apopilo") onApopilo();
                    else onPersonal();
                  }}
                  className={`w-full flex items-center gap-4 h-[52px] px-4 bg-transparent border-0 text-left ${
                    enabled ? "cursor-pointer hover:bg-[var(--bg-hover)] text-[var(--foreground)]" : "cursor-default text-[var(--foreground-muted)]"
                  }`}
                >
                  <Icon size={22} aria-hidden={true} className={enabled ? "text-[var(--foreground)]" : "text-[var(--foreground-subtle)]"} />
                  <span className="flex-1 text-[15px]">{label}</span>
                  {!enabled && <span className="text-xs text-[var(--foreground-subtle)]">{t("personal_soon", "Soon")}</span>}
                </button>
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
